import React from 'react';
import { Box, useTheme } from '@mui/material';

import {
  getVerticalStackSx,
  Page,
  RowTypes,
  Switch,
  Table,
  TableSearchBar,
  VerticalStack,
} from 'components';
import { getNumberOfPages } from 'core/shared';
import { useDelayedInput } from 'hooks/useDelayedInput';
import { useNavigate } from 'hooks/useNavigate';
import { useSearchParams } from 'hooks/useSearchParams';
import { useTranslations } from 'hooks/useTranslations';

import { UseQuery } from 'useQuery';

function OrganisationsPage() {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = React.useState('');
  const { searchParams, setSearchParams } = useSearchParams('organisations');

  const delayedSearchQuery = useDelayedInput({
    input: searchQuery,
    onChange: (value: string) => {
      if (value) {
        setSearchParams({ page: 1, q: value });
        return;
      }

      setSearchParams({ page, q: value });
    },
    path: '/organisations',
  });

  const [page, setPage] = React.useState(1);
  // Off by default, so the list shows every organisation.
  const [activeForInvoicing, setActiveForInvoicing] = React.useState(false);
  const { data: organisationsData, isFetching } = UseQuery.organisations({
    page,
    query: delayedSearchQuery,
    activeForInvoicing,
  });

  React.useEffect(() => {
    if (searchParams.page) {
      setPage(searchParams.page);
    }

    if (searchParams.q) {
      setSearchQuery(searchParams.q);
    }
  }, [searchParams]);

  const translations = useTranslations('organisationsPage');

  const { navigate } = useNavigate();

  return (
    <Page
      name="organisations"
      actions={[
        {
          svgName: 'add',
          title: translations.addOrganisation,
          mode: 'closeOnClick',
          onClick: () => {
            navigate({ name: 'organisation', id: 'new', searchParams: { tab: 0 }, metaKey: false });
          },
        },
      ]}
    >
      <>
        <VerticalStack>
          {}
          <Box
            sx={{
              padding: 2,
              backgroundColor: 'background.paper',
              display: 'flex',
              ...getVerticalStackSx({ theme, verticalPosition: 'top' }),
            }}
          >
            <Switch
              label={translations.showOnlyActiveForInvoicing}
              checked={activeForInvoicing}
              onChange={(checked) => {
                setActiveForInvoicing(checked);
                setPage(1);
                setSearchParams({ page: 1, q: searchQuery });
              }}
            />
          </Box>
          <TableSearchBar
            placeholder={translations.organisations}
            label={translations.searchOrganisations}
            value={searchQuery}
            onChange={(value: string) => {
              setSearchQuery(value);
            }}
            fullWidth={true}
          />
          <Table
            columns={{
              name: { name: translations.name, type: 'text', flex: 1 },
              contactEmailAddress: {
                name: translations.contactEmailAddress,
                type: 'text',
                flex: 1,
              },
              organisationId: { name: translations.id, type: 'text', flex: 1 },
              activeForInvoicing: {
                name: translations.activeForInvoicing,
                type: 'textOrSVG',
                width: 160,
              },
            }}
            rows={(organisationsData?.result ?? []).map(
              ({ id, name, contact, active_for_invoicing: isActiveForInvoicing }) => ({
                id,
                name,
                contactEmailAddress: contact?.email ?? '-',
                organisationId: id,
                activeForInvoicing: isActiveForInvoicing
                  ? ({
                      svgName: 'check',
                      tooltip: translations.activeForInvoicing,
                    } as RowTypes['textOrSVG'])
                  : '',
              }),
            )}
            onClickRow={({ id, metaKey }) => {
              navigate({
                name: 'organisation',
                id: id.toString(),
                searchParams: { tab: 0 },
                metaKey,
              });
            }}
            pagination={{
              count: getNumberOfPages({ meta: organisationsData?.meta }),
              page,
              onChange: (page) => {
                setPage(page);
                setSearchParams({ page, q: searchQuery });
              },
            }}
            loading={isFetching}
          />
        </VerticalStack>
      </>
    </Page>
  );
}

export { OrganisationsPage };
